import { useQuery } from '@tanstack/react-query';
import { fetchActiveSessions, type SessionData } from '@/lib/api';
import { useStreamAwareInterval } from '@/store/sessionStream';

/**
 * The ONE query key for the driver's live-session rows. Every consumer
 * (the live home, ChargingPage, MiniChargingBar, QuickActions, the SSE
 * invalidation in sessionEventStream) shares this key so they can never
 * disagree about whether a session exists. ChargingPage used to poll a
 * second key (['active-sessions']) at 4s next to this one at 15s, and
 * the two caches could show the home a session the page had already
 * seen end.
 */
export const ACTIVE_SESSION_KEY = ['sessions', 'active'] as const;

/**
 * All live rows for the driver — 'active' first, then any 'pending'
 * shell (the plug-in window between the Start tap and the charger's
 * StartTransaction), or the single 'finishing' row (plug still attached
 * after StopTransaction). Server-ordered; see GET /sessions/active.
 *
 * `baseIntervalMs` is the poll cadence while the SSE stream is not
 * open (no poll when it is open with live events, which carry every
 * meter sample; a 60s heartbeat when open without them). The 5s arm
 * while finishing or idle stays: idle fees grow with the clock.
 * Observers of the same key each
 * schedule their own refetch and share the cache, so the surface that
 * needs the fastest cadence (the live view, 4s) sets the pace for
 * everyone while it is mounted; the ambient consumers stay at 15s.
 *   - paused when backgrounded (refetchIntervalInBackground: false)
 *   - `refetchOnWindowFocus` snaps fresh on tab return so a backgrounded
 *     completion shows immediately when the user comes back
 * The 10s `staleTime` prevents component remounts from spamming the
 * endpoint when navigating between routes.
 */
export function useActiveSessionRows(baseIntervalMs = 15_000) {
  const refetchInterval = useStreamAwareInterval(baseIntervalMs);
  return useQuery<SessionData[], Error>({
    queryKey: ACTIVE_SESSION_KEY,
    queryFn: fetchActiveSessions,
    staleTime: 10_000,
    refetchInterval: query => query.state.data?.some(s => s.phase === 'finishing' || s.idle_view?.cable_connected) ? 5000 : refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Returns the driver's current session row, or null: the active one
 * when there is one, else the pending shell, else the finishing tail.
 *
 * The /sessions/active endpoint is already user-scoped server-side
 * (it filters by `request.user.sub`), so we can safely treat the
 * first row as the driver's "the" session — concurrent active
 * sessions are theoretically possible (start at two stalls) but
 * vanishingly rare. Server ordering puts the drawing session first.
 *
 * `data` is `undefined` until the first fetch resolves and `null` once
 * it has and there is nothing — callers that must not act on "unknown"
 * (the tour gate, the home route) check for undefined explicitly.
 */
export function useActiveSession() {
  const refetchInterval = useStreamAwareInterval(15_000);
  return useQuery<SessionData[], Error, SessionData | null>({
    queryKey: ACTIVE_SESSION_KEY,
    queryFn: fetchActiveSessions,
    select: (rows) => rows[0] ?? null,
    staleTime: 10_000,
    refetchInterval: query => query.state.data?.some(s => s.phase === 'finishing' || s.idle_view?.cable_connected) ? 5000 : refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}
